type SoundName = "jump" | "fruit" | "pin" | "power" | "bounce" | "hurt" | "checkpoint" | "win";
type AudioBus = "music" | "sfx";
type MusicTheme =
  | "grove"
  | "burrow"
  | "treetop"
  | "orchard"
  | "sunset"
  | "shore"
  | "fizz-falls"
  | "tidepool"
  | "boardwalk"
  | "marina"
  | "crown-vault";

const MUSIC_KEY = "uppg-music-volume";
const SFX_KEY = "uppg-sfx-volume";
const NOTES: Record<MusicTheme, number[]> = {
  grove: [261.63, 329.63, 392, 523.25, 659.25],
  burrow: [196, 246.94, 293.66, 392, 493.88],
  treetop: [293.66, 369.99, 440, 587.33, 739.99],
  orchard: [261.63, 349.23, 392, 523.25, 698.46],
  sunset: [220, 277.18, 329.63, 440, 554.37],
  shore: [293.66, 369.99, 440, 587.33, 739.99],
  "fizz-falls": [329.63, 392, 493.88, 659.25, 783.99],
  tidepool: [220, 293.66, 349.23, 440, 587.33],
  boardwalk: [261.63, 329.63, 440, 523.25, 659.25],
  marina: [196, 246.94, 329.63, 392, 493.88],
  "crown-vault": [220, 277.18, 369.99, 440, 554.37],
};

function storedVolume(key: string, fallback: number): number {
  const value = Number(localStorage.getItem(key));
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : fallback;
}

export class GameAudio {
  private context?: AudioContext;
  private master?: GainNode;
  private musicBus?: GainNode;
  private sfxBus?: GainNode;
  private muted = localStorage.getItem("uppg-muted") === "true";
  private musicVolume = storedVolume(MUSIC_KEY, 0.7);
  private sfxVolume = storedVolume(SFX_KEY, 0.85);
  private musicStep = 0;
  private nextMusicAt = 0;
  private theme: MusicTheme = "grove";

  get isMuted(): boolean {
    return this.muted;
  }

  get musicLevel(): number {
    return this.musicVolume;
  }

  get sfxLevel(): number {
    return this.sfxVolume;
  }

  unlock(): void {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.musicBus = this.context.createGain();
      this.sfxBus = this.context.createGain();
      this.master.gain.value = this.muted ? 0 : 0.17;
      this.musicBus.gain.value = this.musicVolume;
      this.sfxBus.gain.value = this.sfxVolume;
      this.musicBus.connect(this.master);
      this.sfxBus.connect(this.master);
      this.master.connect(this.context.destination);
    }
    void this.context.resume();
  }

  toggle(): boolean {
    this.unlock();
    this.muted = !this.muted;
    localStorage.setItem("uppg-muted", String(this.muted));
    this.applyGain(this.master, this.muted ? 0 : 0.17);
    return this.muted;
  }

  setMusicVolume(value: number): void {
    this.musicVolume = Math.max(0, Math.min(1, value));
    localStorage.setItem(MUSIC_KEY, String(this.musicVolume));
    this.applyGain(this.musicBus, this.musicVolume);
  }

  setSfxVolume(value: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, value));
    localStorage.setItem(SFX_KEY, String(this.sfxVolume));
    this.applyGain(this.sfxBus, this.sfxVolume);
  }

  setTheme(theme: MusicTheme): void {
    if (theme === this.theme) return;
    this.theme = theme;
    this.musicStep = 0;
    this.nextMusicAt = 0;
  }

  updateMusic(running: boolean): void {
    if (!running || this.muted || !this.context || !this.master) return;
    if (this.context.currentTime < this.nextMusicAt) return;

    const notes = NOTES[this.theme];
    const patterns: Record<MusicTheme, number[]> = {
      grove: [0, 2, 1, 3, 2, 4, 3, 1, 0, 2, 4, 2, 1, 3, 2, 0],
      burrow: [0, 1, 0, 2, 1, 3, 1, 0, 2, 1, 4, 2, 1, 0, 1, 0],
      treetop: [0, 2, 4, 3, 2, 4, 1, 3, 0, 2, 3, 4, 2, 1, 3, 2],
      orchard: [0, 2, 3, 2, 4, 3, 1, 2, 0, 3, 4, 2, 1, 3, 2, 0],
      sunset: [0, 1, 3, 2, 1, 4, 3, 1, 0, 2, 3, 1, 2, 1, 0, 0],
      shore: [0, 2, 4, 2, 1, 3, 4, 3, 0, 2, 3, 1, 4, 2, 1, 0],
      "fizz-falls": [0, 3, 2, 4, 1, 3, 4, 2, 0, 2, 4, 3, 1, 4, 2, 3],
      tidepool: [0, 1, 3, 2, 1, 0, 2, 4, 3, 1, 2, 0, 1, 3, 2, 0],
      boardwalk: [0, 2, 3, 4, 2, 1, 3, 2, 0, 4, 3, 1, 2, 4, 3, 2],
      marina: [0, 1, 2, 3, 1, 4, 3, 2, 0, 2, 3, 1, 4, 2, 1, 0],
      "crown-vault": [0, 2, 4, 3, 1, 4, 2, 3, 0, 3, 4, 1, 2, 4, 3, 0],
    };
    const pattern = patterns[this.theme];
    const note = notes[pattern[this.musicStep % pattern.length]];
    const interval = this.theme === "orchard" || this.theme === "boardwalk"
      ? 0.2
      : this.theme === "burrow" || this.theme === "tidepool" || this.theme === "marina"
        ? 0.31
        : this.theme === "crown-vault"
          ? 0.19
          : 0.24;
    const wave: OscillatorType = this.theme === "burrow" || this.theme === "tidepool"
      ? "sine"
      : this.theme === "sunset" || this.theme === "marina" || this.theme === "crown-vault"
        ? "triangle"
        : "square";
    this.tone(note, interval * 0.55, wave, 0.1, 0, "music");
    if (this.musicStep % 4 === 0) this.tone(note / 2, interval * 0.85, "sine", 0.075, 0, "music");
    if (this.theme === "treetop" && this.musicStep % 8 === 6) {
      this.tone(note * 2, 0.08, "triangle", 0.045, 0.055, "music");
    }
    if ((this.theme === "shore" || this.theme === "fizz-falls") && this.musicStep % 8 === 4) {
      this.tone(note * 1.5, 0.07, "sine", 0.05, 0.04, "music");
    }
    this.musicStep += 1;
    this.nextMusicAt = this.context.currentTime + interval;
  }

  play(name: SoundName): void {
    if (this.muted) return;
    this.unlock();

    switch (name) {
      case "jump": this.sweep(250, 520, 0.11, "square", 0.22); break;
      case "fruit":
        this.tone(660, 0.06, "square", 0.18);
        this.tone(880, 0.07, "square", 0.13, 0.045);
        break;
      case "pin":
        [523, 659, 784, 1047].forEach((note, index) => this.tone(note, 0.12, "triangle", 0.22, index * 0.07));
        break;
      case "power": this.sweep(240, 980, 0.32, "triangle", 0.24); break;
      case "bounce": this.sweep(180, 440, 0.09, "square", 0.17); break;
      case "hurt": this.sweep(260, 95, 0.24, "sawtooth", 0.18); break;
      case "checkpoint":
        [392, 523, 659].forEach((note, index) => this.tone(note, 0.13, "triangle", 0.2, index * 0.1));
        break;
      case "win":
        [392, 523, 659, 784, 1047].forEach((note, index) => this.tone(note, 0.24, "triangle", 0.22, index * 0.11));
        break;
    }
  }

  private applyGain(node: GainNode | undefined, value: number): void {
    if (!node || !this.context) return;
    node.gain.cancelScheduledValues(this.context.currentTime);
    node.gain.setTargetAtTime(value, this.context.currentTime, 0.015);
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    delay = 0,
    bus: AudioBus = "sfx",
  ): void {
    const destination = bus === "music" ? this.musicBus : this.sfxBus;
    if (!this.context || !destination) return;
    const start = this.context.currentTime + delay;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain);
    gain.connect(destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  }

  private sweep(from: number, to: number, duration: number, type: OscillatorType, volume: number): void {
    if (!this.context || !this.sfxBus) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(from, now);
    oscillator.frequency.exponentialRampToValueAtTime(to, now + duration);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.sfxBus);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }
}
